import React, { useState, useEffect, useRef } from 'react';
import SockJS from 'sockjs-client';
import { Client } from '@stomp/stompjs';
import { useAuth } from '../auth/AuthContext';
import chatInternoService from '../services/chatInternoService';
import { getClientes } from '../services/clienteService';
import { getTramites } from '../services/tramiteService';
import '../style/components/ChatInternoPanel.css';
import { coincideBusqueda } from '../utils/textoComparable';

const ChatInternoPanel = ({ isOpen, onClose, onUnreadChange }) => {
    const { user } = useAuth();
    const panelRef = useRef(null);

    const [busqueda, setBusqueda] = useState('');
    const [usuariosSistema, setUsuariosSistema] = useState([]);
    const [destacados, setDestacados] = useState([]);
    const [grupales, setGrupales] = useState([]);
    const [privados, setPrivados] = useState([]);
    const [contactos, setContactos] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadingUsuarios, setLoadingUsuarios] = useState(false);
    const [seccionesOpen, setSeccionesOpen] = useState({
        destacados: true,
        grupales: true,
        privados: true,
    });
    const [conversacionActiva, setConversacionActiva] = useState(null);
    const [mensajesConversacion, setMensajesConversacion] = useState([]);
    const [textoMensaje, setTextoMensaje] = useState('');
    const mensajesEndRef = useRef(null);
    const [menuAdjuntoOpen, setMenuAdjuntoOpen] = useState(false);
    const [selectorTramiteOpen, setSelectorTramiteOpen] = useState(false);
    const [selectorClienteOpen, setSelectorClienteOpen] = useState(false);
    const [listaTramites, setListaTramites] = useState([]);
    const [listaClientes, setListaClientes] = useState([]);
    const [cargandoTramites, setCargandoTramites] = useState(false);
    const [cargandoClientes, setCargandoClientes] = useState(false);
    const inputImagenRef = useRef(null);

    const [crearGrupoOpen, setCrearGrupoOpen] = useState(false);
    const [nombreNuevoGrupo, setNombreNuevoGrupo] = useState('');
    const [participantesSeleccionados, setParticipantesSeleccionados] = useState([]);
    const [creandoGrupo, setCreandoGrupo] = useState(false);

    const [detalleOpen, setDetalleOpen] = useState(false);
    const [detalleConversacion, setDetalleConversacion] = useState(null);
    const [loadingDetalle, setLoadingDetalle] = useState(false);
    const [editandoNombreGrupo, setEditandoNombreGrupo] = useState(false);
    const [nombreGrupoEdit, setNombreGrupoEdit] = useState('');
    const [agregarUsuarioGrupoOpen, setAgregarUsuarioGrupoOpen] = useState(false);

    const [confirmModal, setConfirmModal] = useState({ open: false, title: '', message: '', onConfirm: null, onCancel: null });
    const [errorIniciarChat, setErrorIniciarChat] = useState(null);
    const onUnreadChangeRef = useRef(onUnreadChange);
    const mensajesScrollRef = useRef(null);
    const mantenerScrollAbajoRef = useRef(true);
    onUnreadChangeRef.current = onUnreadChange;

    const toggleSeccion = (key) => {
        setSeccionesOpen((prev) => ({ ...prev, [key]: !prev[key] }));
    };

    const toggleParticipanteGrupo = (idUsuario) => {
        setParticipantesSeleccionados((prev) =>
            prev.includes(idUsuario) ? prev.filter((id) => id !== idUsuario) : [...prev, idUsuario]
        );
    };

    const crearGrupo = () => {
        const nombre = (nombreNuevoGrupo || '').trim();
        if (!nombre) return;
        setCreandoGrupo(true);
        chatInternoService
            .crearConversacionGrupal(nombre, participantesSeleccionados)
            .then(() => {
                setCrearGrupoOpen(false);
                setNombreNuevoGrupo('');
                setParticipantesSeleccionados([]);
                loadConversaciones();
            })
            .catch(() => {})
            .finally(() => setCreandoGrupo(false));
    };

    const cerrarModalCrearGrupo = () => {
        setCrearGrupoOpen(false);
        setNombreNuevoGrupo('');
        setParticipantesSeleccionados([]);
    };

    const abrirDetalle = () => {
        if (!conversacionActiva?.id) return;
        setLoadingDetalle(true);
        setDetalleOpen(true);
        setDetalleConversacion(null);
        chatInternoService.getConversacion(conversacionActiva.id)
            .then((res) => {
                setDetalleConversacion(res.data);
                setNombreGrupoEdit(res.data.nombre || '');
            })
            .catch(() => setDetalleOpen(false))
            .finally(() => setLoadingDetalle(false));
    };

    const cerrarDetalle = () => {
        setDetalleOpen(false);
        setDetalleConversacion(null);
        setEditandoNombreGrupo(false);
        setAgregarUsuarioGrupoOpen(false);
    };

    const toggleDestacar = () => {
        if (!detalleConversacion?.id) return;
        const nuevoEstado = !detalleConversacion.esDestacado;
        chatInternoService.marcarDestacado(detalleConversacion.id, nuevoEstado)
            .then(() => chatInternoService.getConversacion(detalleConversacion.id))
            .then((res) => setDetalleConversacion(res.data))
            .then(() => loadConversaciones(true))
            .catch(() => {});
    };

    const guardarNombreGrupo = () => {
        if (!detalleConversacion?.id || !nombreGrupoEdit.trim()) return;
        chatInternoService.actualizarGrupo(detalleConversacion.id, { nombre: nombreGrupoEdit.trim() })
            .then((res) => {
                setDetalleConversacion(res.data);
                setConversacionActiva((prev) => prev ? { ...prev, nombre: res.data.nombre } : null);
                setEditandoNombreGrupo(false);
            })
            .catch(() => {});
    };

    const quitarParticipanteGrupo = (idUsuario) => {
        if (!detalleConversacion?.id) return;
        const idUsuarioNum = Number(idUsuario);
        if (Number.isNaN(idUsuarioNum)) return;
        setConfirmModal({
            open: true,
            title: 'Quitar del grupo',
            message: '¿Quitar a este usuario del grupo?',
            onConfirm: () => {
                setConfirmModal((prev) => ({ ...prev, open: false }));
                const idConv = detalleConversacion.id;
                // Actualización optimista: quitar de la lista de inmediato (comparación numérica)
                setDetalleConversacion((prev) => {
                    if (!prev?.participantes) return prev;
                    return {
                        ...prev,
                        participantes: prev.participantes.filter((p) => Number(p.idUsuario) !== idUsuarioNum),
                    };
                });
                chatInternoService.quitarParticipante(idConv, idUsuarioNum)
                    .then(() => {
                        // No recargar desde el servidor: la lista ya está correcta y así evitamos que reaparezca por caché/datos viejos
                    })
                    .catch((err) => {
                        console.error('Error al quitar participante:', err);
                        // Solo si falla: volver a cargar desde el servidor para restaurar la lista real
                        chatInternoService.getConversacion(idConv).then((r) => setDetalleConversacion(r.data)).catch(() => {});
                    });
            },
            onCancel: () => setConfirmModal((prev) => ({ ...prev, open: false })),
        });
    };

    const agregarUsuarioAlGrupo = (idUsuario) => {
        if (!detalleConversacion?.id) return;
        chatInternoService.agregarParticipante(detalleConversacion.id, idUsuario)
            .then(() => chatInternoService.getConversacion(detalleConversacion.id))
            .then((res) => {
                setDetalleConversacion(res.data);
                setAgregarUsuarioGrupoOpen(false);
            })
            .catch(() => {});
    };

    const eliminarGrupo = () => {
        if (!detalleConversacion?.id) return;
        setConfirmModal({
            open: true,
            title: 'Eliminar grupo',
            message: '¿Eliminar este grupo? Se perderán todos los mensajes.',
            onConfirm: () => {
                setConfirmModal((prev) => ({ ...prev, open: false }));
                chatInternoService.eliminarConversacion(detalleConversacion.id)
                    .then(() => {
                        cerrarDetalle();
                        volverALista();
                        loadConversaciones();
                    })
                    .catch(() => {});
            },
            onCancel: () => setConfirmModal((prev) => ({ ...prev, open: false })),
        });
    };

    const eliminarConversacionPrivada = () => {
        if (!detalleConversacion?.id) return;
        setConfirmModal({
            open: true,
            title: 'Eliminar conversación',
            message: '¿Eliminar esta conversación?',
            onConfirm: () => {
                setConfirmModal((prev) => ({ ...prev, open: false }));
                chatInternoService.eliminarConversacion(detalleConversacion.id)
                    .then(() => {
                        cerrarDetalle();
                        volverALista();
                        loadConversaciones();
                    })
                    .catch(() => {});
            },
            onCancel: () => setConfirmModal((prev) => ({ ...prev, open: false })),
        });
    };

    const apiOrigin = process.env.REACT_APP_API_URL || 'http://localhost:8080';

    const loadConversaciones = (silent = false) => {
        if (!user?.id) return;
        if (!silent) setLoading(true);
        chatInternoService.getConversaciones()
            .then((res) => {
                const list = Array.isArray(res.data) ? res.data : [];
                setDestacados(list.filter((c) => c.esDestacado));
                setGrupales(list.filter((c) => c.tipo === 'GRUPO'));
                setPrivados(list.filter((c) => c.tipo === 'PRIVADO'));
                setContactos(list.filter((c) => c.tipo === 'PRIVADO'));
            })
            .catch(() => {
                setDestacados([]);
                setGrupales([]);
                setPrivados([]);
                setContactos([]);
            })
            .finally(() => { if (!silent) setLoading(false); });
    };

    useEffect(() => {
        if (!isOpen || !user?.id) return;
        setLoading(true);
        setLoadingUsuarios(true);
        Promise.all([
            chatInternoService.getConversaciones(),
            chatInternoService.getUsuariosSistema(),
        ])
            .then(([resConv, resU]) => {
                const list = Array.isArray(resConv.data) ? resConv.data : [];
                setDestacados(list.filter((c) => c.esDestacado));
                setGrupales(list.filter((c) => c.tipo === 'GRUPO'));
                setPrivados(list.filter((c) => c.tipo === 'PRIVADO'));
                setContactos(list.filter((c) => c.tipo === 'PRIVADO'));
                const usuariosData = resU.data || [];
                setUsuariosSistema(Array.isArray(usuariosData) ? usuariosData.filter((u) => u.id !== user.id) : []);
            })
            .catch(() => {
                setDestacados([]);
                setGrupales([]);
                setPrivados([]);
                setContactos([]);
                setUsuariosSistema([]);
            })
            .finally(() => {
                setLoading(false);
                setLoadingUsuarios(false);
            });
    }, [isOpen, user?.id]);

    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape') onClose();
        };
        if (isOpen) {
            document.addEventListener('keydown', handleEscape);
            document.body.style.overflow = 'hidden';
        }
        return () => {
            document.removeEventListener('keydown', handleEscape);
            document.body.style.overflow = '';
        };
    }, [isOpen, onClose]);

    const filtrar = (lista, nombreKey = 'nombre') => {
        if (!busqueda.trim()) return lista;
        return lista.filter((item) => {
            const nombre = item[nombreKey] || item.titulo || item.nombreCompleto || '';
            return coincideBusqueda(String(nombre), busqueda);
        });
    };

    const usuariosBusqueda = React.useMemo(() => {
        if (!busqueda.trim()) return [];
        return usuariosSistema.filter((u) => coincideBusqueda([u.nombre, u.email], busqueda));
    }, [busqueda, usuariosSistema]);

    const inicialNombre = (item) => {
        const n = item.nombre || item.titulo || item.nombreCompleto || '?';
        return n.charAt(0).toUpperCase();
    };

    const getNombreConversacion = (conv) => {
        if (!conv) return 'Usuario';
        if (conv.tipo === 'GRUPO') return conv.nombre || 'Grupo';
        const otro = conv.participantes?.find((p) => p.idUsuario !== user?.id);
        return otro?.nombre || otro?.email || conv.nombre || 'Usuario';
    };

    const getPreviewUltimoMensaje = (m) => {
        if (!m) return '';
        if (m.contenidoTexto) return m.contenidoTexto.length > 40 ? m.contenidoTexto.slice(0, 40) + '…' : m.contenidoTexto;
        if (m.tipo === 'IMAGEN') return '[Imagen]';
        if (m.tipo === 'TRAMITE') return '[Trámite]';
        if (m.tipo === 'CLIENTE') return '[Cliente]';
        if (m.tipo === 'SISTEMA') return '[Info]';
        return '';
    };

    const abrirConversacion = (tipo, id, nombre) => {
        setConversacionActiva({ tipo, id, nombre });
        setMensajesConversacion([]);
        setTextoMensaje('');
        if (!id) return;
        setLoading(true);
        chatInternoService
            .getMensajes(id)
            .then((res) => {
                const list = Array.isArray(res.data) ? res.data : [];
                const mapped = list.map((m) => ({
                    id: m.id,
                    tipo: (m.tipo || 'TEXTO').toLowerCase(),
                    esMio: m.idUsuarioEmisor === user?.id,
                    fecha: m.fechaCreacion,
                    contenido: m.contenidoTexto,
                    urlImagen: m.urlImagen,
                    nombreEmisor: m.nombreEmisor,
                    ...(m.metadataJson ? (() => { try { return JSON.parse(m.metadataJson); } catch { return {}; } })() : {}),
                }));
                setMensajesConversacion(mapped);
                chatInternoService.marcarComoLeido(id).then(() => {
                    onUnreadChangeRef.current?.();
                    loadConversaciones(true);
                });
            })
            .catch(() => setMensajesConversacion([]))
            .finally(() => setLoading(false));
    };

    const volverALista = () => {
        setConversacionActiva(null);
        setMensajesConversacion([]);
        setErrorIniciarChat(null);
    };

    const iniciarChatConUsuario = (u) => {
        const nombreUsuario = u.nombre || u.email || 'Usuario';
        setConversacionActiva({ tipo: 'privado', id: null, nombre: nombreUsuario, pendingUserId: u.id, pendingUser: u });
        setMensajesConversacion([]);
        setBusqueda('');
        setErrorIniciarChat(null);
        setLoading(true);
        chatInternoService
            .crearConversacionPrivada(u.id)
            .then((res) => {
                const conv = res?.data != null ? res.data : res;
                const id = conv?.id;
                if (!id) return Promise.reject(new Error('No se obtuvo la conversación'));
                const nombre = getNombreConversacion(conv) || nombreUsuario;
                setConversacionActiva((prev) => (prev?.pendingUserId === u.id ? { ...prev, id, nombre, pendingUserId: undefined, pendingUser: undefined } : prev));
                setErrorIniciarChat(null);
                return chatInternoService.getMensajes(id).then((msgRes) => ({ idConversacion: id, msgRes }));
            })
            .then(({ idConversacion, msgRes }) => {
                const list = Array.isArray(msgRes?.data) ? msgRes.data : [];
                const mapped = list.map((m) => ({
                    id: m.id,
                    tipo: (m.tipo || 'TEXTO').toLowerCase(),
                    esMio: m.idUsuarioEmisor === user?.id,
                    fecha: m.fechaCreacion,
                    contenido: m.contenidoTexto,
                    urlImagen: m.urlImagen,
                    nombreEmisor: m.nombreEmisor,
                    ...(m.metadataJson ? (() => { try { return JSON.parse(m.metadataJson); } catch { return {}; } })() : {}),
                }));
                setMensajesConversacion(mapped);
                chatInternoService.marcarComoLeido(idConversacion).then(() => {
                    onUnreadChangeRef.current?.();
                    loadConversaciones(true);
                });
            })
            .catch(() => {
                setMensajesConversacion([]);
                setErrorIniciarChat(u.id);
            })
            .finally(() => setLoading(false));
    };

    const mapApiMensajeToLocal = (m) => ({
        id: m.id,
        tipo: (m.tipo || 'TEXTO').toLowerCase(),
        esMio: m.idUsuarioEmisor === user?.id,
        fecha: m.fechaCreacion,
        contenido: m.contenidoTexto,
        urlImagen: m.urlImagen ? (m.urlImagen.startsWith('http') ? m.urlImagen : apiOrigin + m.urlImagen) : undefined,
        nombreEmisor: m.nombreEmisor,
        ...(m.metadataJson ? (() => { try { return JSON.parse(m.metadataJson); } catch { return {}; } })() : {}),
    });

    const anadirMensajeAlEstado = (m) => {
        const mapped = mapApiMensajeToLocal(m);
        setMensajesConversacion((prev) => {
            if (prev.some((msg) => msg.id === mapped.id)) return prev;
            return [...prev, mapped];
        });
        setMenuAdjuntoOpen(false);
        setSelectorTramiteOpen(false);
        setSelectorClienteOpen(false);
        if (mantenerScrollAbajoRef.current) {
            setTimeout(() => mensajesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
        }
    };

    const enviarMensaje = (e) => {
        e.preventDefault();
        if (!textoMensaje.trim() || !conversacionActiva?.id) return;
        const texto = textoMensaje.trim();
        setTextoMensaje('');
        chatInternoService
            .enviarMensaje(conversacionActiva.id, { tipo: 'TEXTO', contenidoTexto: texto })
            .then((res) => anadirMensajeAlEstado(res.data))
            .catch(() => setTextoMensaje(texto));
    };

    const onSeleccionarImagen = (e) => {
        const file = e.target?.files?.[0];
        if (!file || !file.type.startsWith('image/') || !conversacionActiva?.id) return;
        setMenuAdjuntoOpen(false);
        chatInternoService
            .subirAdjunto(file)
            .then((up) => {
                const url = up.data?.url;
                if (!url) return Promise.reject();
                const urlCompleta = url.startsWith('http') ? url : apiOrigin + url;
                return chatInternoService.enviarMensaje(conversacionActiva.id, { tipo: 'IMAGEN', urlImagen: urlCompleta });
            })
            .then((res) => anadirMensajeAlEstado(res.data))
            .catch(() => {});
        e.target.value = '';
    };

    const abrirSelectorTramite = () => {
        setMenuAdjuntoOpen(false);
        setSelectorTramiteOpen(true);
        setCargandoTramites(true);
        getTramites()
            .then((data) => setListaTramites(Array.isArray(data) ? data : []))
            .catch(() => setListaTramites([]))
            .finally(() => setCargandoTramites(false));
    };

    const abrirSelectorCliente = () => {
        setMenuAdjuntoOpen(false);
        setSelectorClienteOpen(true);
        setCargandoClientes(true);
        getClientes()
            .then((data) => setListaClientes(Array.isArray(data) ? data : []))
            .catch(() => setListaClientes([]))
            .finally(() => setCargandoClientes(false));
    };

    const enviarTramite = (t) => {
        if (!conversacionActiva?.id) return;
        const idTramite = t.idTramite ?? t.id;
        const nombreTramite = t.nombreTipoTramite || `Trámite #${idTramite}`;
        const metadata = JSON.stringify({ idTramite, nombreTramite });
        chatInternoService.enviarMensaje(conversacionActiva.id, { tipo: 'TRAMITE', metadataJson: metadata }).then((res) => anadirMensajeAlEstado(res.data)).catch(() => {});
    };

    const enviarCliente = (c) => {
        if (!conversacionActiva?.id) return;
        const nombreCliente = [c.nombre, c.apellido].filter(Boolean).join(' ') || c.cuil || 'Cliente';
        const metadata = JSON.stringify({ cuil: c.cuil, nombreCliente });
        chatInternoService.enviarMensaje(conversacionActiva.id, { tipo: 'CLIENTE', metadataJson: metadata }).then((res) => anadirMensajeAlEstado(res.data)).catch(() => {});
    };

    const enviarInfoSistema = (titulo, enlace, descripcion) => {
        if (!conversacionActiva?.id) return;
        setMenuAdjuntoOpen(false);
        const metadata = JSON.stringify({ titulo, enlace, descripcion });
        chatInternoService.enviarMensaje(conversacionActiva.id, { tipo: 'SISTEMA', metadataJson: metadata }).then((res) => anadirMensajeAlEstado(res.data)).catch(() => {});
    };

    useEffect(() => {
        if (conversacionActiva && mantenerScrollAbajoRef.current && mensajesEndRef.current) {
            mensajesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [mensajesConversacion, conversacionActiva]);

    // Chat en tiempo real: WebSocket STOMP para recibir mensajes sin F5
    useEffect(() => {
        const id = conversacionActiva?.id;
        if (!isOpen || !id) return;
        const wsBase = (process.env.REACT_APP_API_URL || 'http://localhost:8080/api').replace(/\/api\/?$/, '');
        const sockUrl = wsBase + '/ws';
        const client = new Client({
            webSocketFactory: () => new SockJS(sockUrl),
            reconnectDelay: 5000,
            heartbeatIncoming: 4000,
            heartbeatOutgoing: 4000,
        });
        let subscription;
        client.onConnect = () => {
            subscription = client.subscribe('/topic/chat-interno/' + id, (message) => {
                try {
                    const body = JSON.parse(message.body);
                    const mapped = {
                        id: body.id,
                        tipo: (body.tipo || 'TEXTO').toLowerCase(),
                        esMio: body.idUsuarioEmisor === user?.id,
                        fecha: body.fechaCreacion,
                        contenido: body.contenidoTexto,
                        urlImagen: body.urlImagen ? (body.urlImagen.startsWith('http') ? body.urlImagen : apiOrigin + body.urlImagen) : undefined,
                        nombreEmisor: body.nombreEmisor,
                        ...(body.metadataJson ? (() => { try { return JSON.parse(body.metadataJson); } catch { return {}; } })() : {}),
                    };
                    setMensajesConversacion((prev) => {
                        if (prev.some((m) => m.id === mapped.id)) return prev;
                        return [...prev, mapped];
                    });
                    setTimeout(() => mensajesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
                    chatInternoService.marcarComoLeido(id).then(() => {
                        onUnreadChangeRef.current?.();
                        loadConversaciones(true);
                    });
                } catch (e) {
                    console.warn('Chat WS parse error', e);
                }
            });
        };
        client.activate();
        return () => {
            if (subscription) subscription.unsubscribe();
            client.deactivate();
        };
    }, [isOpen, conversacionActiva?.id, user?.id]);

    if (!isOpen) return null;

    const filtrarPorNombreConv = (lista) => {
        if (!busqueda.trim()) return lista;
        const q = busqueda.toLowerCase().trim();
        return lista.filter((item) => getNombreConversacion(item).toLowerCase().includes(q));
    };
    const destacadosF = filtrarPorNombreConv(destacados);
    const grupalesF = filtrarPorNombreConv(grupales);
    const privadosF = filtrarPorNombreConv(privados);

    return (
        <>
            <div className="chat-interno-backdrop" onClick={onClose} aria-hidden="true" />
            <div className="chat-interno-panel-wrap" ref={panelRef} role="dialog" aria-label="Chat interno">
                {conversacionActiva ? (
                    <>
                        <header className="chat-interno-header chat-interno-header-conversacion">
                            <button type="button" className="chat-interno-btn-back" onClick={volverALista} aria-label="Volver a la lista">
                                <i className="bi bi-arrow-left" aria-hidden />
                                <span className="chat-interno-volver-texto">Volver</span>
                            </button>
                            <div className="chat-interno-header-contacto">
                                <div className="chat-interno-item-avatar chat-interno-avatar-header">
                                    {conversacionActiva.nombre ? conversacionActiva.nombre.charAt(0).toUpperCase() : '?'}
                                </div>
                                <div className="chat-interno-header-contacto-info">
                                    <span className="chat-interno-header-nombre">{conversacionActiva.nombre || 'Usuario'}</span>
                                    <span className="chat-interno-header-estado">Desconectado</span>
                                </div>
                            </div>
                            <div className="chat-interno-header-acciones">
                                <button type="button" className="chat-interno-btn-icon" onClick={abrirDetalle} aria-label="Ver detalle">
                                    <i className="bi bi-three-dots-vertical" />
                                </button>
                                <button type="button" className="chat-interno-close" onClick={onClose} aria-label="Cerrar">
                                    <i className="bi bi-x-lg" />
                                </button>
                            </div>
                        </header>
                        <div
                            className="chat-interno-mensajes-wrap"
                            ref={mensajesScrollRef}
                            onScroll={(event) => {
                                const element = event.currentTarget;
                                const estaAbajo = element.scrollHeight - element.scrollTop - element.clientHeight < 80;
                                mantenerScrollAbajoRef.current = estaAbajo;
                            }}
                        >
                            {mensajesConversacion.length === 0 ? (
                                <div className="chat-interno-sin-mensajes">
                                    {errorIniciarChat && conversacionActiva?.pendingUserId === errorIniciarChat ? (
                                        <>
                                            <p className="text-danger">No se pudo abrir la conversación.</p>
                                            <p className="small text-muted">Comprueba la conexión e inténtalo de nuevo.</p>
                                            <button type="button" className="chat-interno-btn-crear-grupo-submit mt-2" onClick={() => conversacionActiva?.pendingUser && iniciarChatConUsuario(conversacionActiva.pendingUser)}>
                                                Reintentar
                                            </button>
                                        </>
                                    ) : loading && !conversacionActiva?.id ? (
                                        <p className="text-muted">Conectando conversación…</p>
                                    ) : (
                                        <>
                                            <p>No hay mensajes aún.</p>
                                            <p className="small text-muted">Escribe un mensaje, adjunta una imagen, comparte un trámite, un cliente o información del sistema.</p>
                                        </>
                                    )}
                                </div>
                            ) : (
                                mensajesConversacion.map((m) => (
                                    <div key={m.id} className={`chat-interno-msg ${m.esMio ? 'chat-interno-msg-mio' : ''}`}>
                                        <div className={`chat-interno-msg-burbuja chat-interno-msg-${m.tipo || 'texto'}`}>
                                            {m.tipo === 'imagen' && m.urlImagen && (
                                                <>
                                                    <img src={m.urlImagen} alt="Adjunto" className="chat-interno-msg-img" />
                                                    <span className="chat-interno-msg-hora">
                                                        {new Date(m.fecha).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })}
                                                    </span>
                                                </>
                                            )}
                                            {m.tipo === 'tramite' && (
                                                <>
                                                    <div className="chat-interno-msg-card">
                                                        <i className="bi bi-folder-fill text-primary me-2" />
                                                        <div>
                                                            <strong>Trámite #{m.idTramite}</strong>
                                                            <div className="small">{m.nombreTramite}</div>
                                                            <a href={`/tramites/${m.idTramite}`} target="_blank" rel="noopener noreferrer" className="chat-interno-msg-link" onClick={() => onClose()}>Ver trámite</a>
                                                        </div>
                                                    </div>
                                                    <span className="chat-interno-msg-hora">{new Date(m.fecha).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })}</span>
                                                </>
                                            )}
                                            {m.tipo === 'cliente' && (
                                                <>
                                                    <div className="chat-interno-msg-card">
                                                        <i className="bi bi-person-fill text-primary me-2" />
                                                        <div>
                                                            <strong>{m.nombreCliente}</strong>
                                                            <div className="small">CUIL: {m.cuil}</div>
                                                            <a href={`/clientes/vista-detallada/${m.cuil}`} target="_blank" rel="noopener noreferrer" className="chat-interno-msg-link" onClick={() => onClose()}>Ver cliente</a>
                                                        </div>
                                                    </div>
                                                    <span className="chat-interno-msg-hora">{new Date(m.fecha).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })}</span>
                                                </>
                                            )}
                                            {m.tipo === 'sistema' && (
                                                <>
                                                    <div className="chat-interno-msg-card chat-interno-msg-card-sistema">
                                                        <i className="bi bi-info-circle-fill text-info me-2" />
                                                        <div>
                                                            <strong>{m.titulo}</strong>
                                                            {m.descripcion && <div className="small">{m.descripcion}</div>}
                                                            {m.enlace && <a href={m.enlace} target="_blank" rel="noopener noreferrer" className="chat-interno-msg-link" onClick={() => onClose()}>Abrir</a>}
                                                        </div>
                                                    </div>
                                                    <span className="chat-interno-msg-hora">{new Date(m.fecha).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })}</span>
                                                </>
                                            )}
                                            {(m.tipo === 'texto' || !m.tipo) && (
                                                <>
                                                    <span className="chat-interno-msg-texto">{m.contenido}</span>
                                                    <span className="chat-interno-msg-hora">{new Date(m.fecha).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })}</span>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                ))
                            )}
                            <div ref={mensajesEndRef} />
                        </div>

                        <input ref={inputImagenRef} type="file" accept="image/*" className="d-none" onChange={onSeleccionarImagen} aria-label="Adjuntar imagen" />

                        {selectorTramiteOpen && (
                            <div className="chat-interno-selector-overlay" onClick={() => setSelectorTramiteOpen(false)}>
                                <div className="chat-interno-selector-panel" onClick={(ev) => ev.stopPropagation()}>
                                    <div className="chat-interno-selector-header">
                                        <span>Compartir trámite</span>
                                        <button type="button" className="chat-interno-close" onClick={() => setSelectorTramiteOpen(false)}><i className="bi bi-x-lg" /></button>
                                    </div>
                                    <div className="chat-interno-selector-list">
                                        {cargandoTramites ? <div className="chat-interno-empty">Cargando trámites…</div> : listaTramites.length === 0 ? <div className="chat-interno-empty">No hay trámites</div> : listaTramites.slice(0, 50).map((t) => (
                                            <button key={t.idTramite ?? t.id} type="button" className="chat-interno-selector-item" onClick={() => enviarTramite(t)}>
                                                <i className="bi bi-folder-fill me-2 text-primary" />
                                                <span>Trámite #{t.idTramite ?? t.id}</span>
                                                {t.nombreTipoTramite && <small className="text-muted ms-2">{t.nombreTipoTramite}</small>}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}

                        {selectorClienteOpen && (
                            <div className="chat-interno-selector-overlay" onClick={() => setSelectorClienteOpen(false)}>
                                <div className="chat-interno-selector-panel" onClick={(ev) => ev.stopPropagation()}>
                                    <div className="chat-interno-selector-header">
                                        <span>Compartir cliente</span>
                                        <button type="button" className="chat-interno-close" onClick={() => setSelectorClienteOpen(false)}><i className="bi bi-x-lg" /></button>
                                    </div>
                                    <div className="chat-interno-selector-list">
                                        {cargandoClientes ? <div className="chat-interno-empty">Cargando clientes…</div> : listaClientes.length === 0 ? <div className="chat-interno-empty">No hay clientes</div> : listaClientes.slice(0, 50).map((c) => (
                                            <button key={c.cuil} type="button" className="chat-interno-selector-item" onClick={() => enviarCliente(c)}>
                                                <i className="bi bi-person-fill me-2 text-primary" />
                                                <span>{[c.nombre, c.apellido].filter(Boolean).join(' ') || c.cuil}</span>
                                                <small className="text-muted ms-2">{c.cuil}</small>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}

                        <form className="chat-interno-input-wrap" onSubmit={enviarMensaje}>
                            <div className="chat-interno-adjunto-wrap">
                                <button type="button" className="chat-interno-btn-adjunto" onClick={() => setMenuAdjuntoOpen(!menuAdjuntoOpen)} aria-label="Adjuntar" aria-expanded={menuAdjuntoOpen}>
                                    <i className="bi bi-paperclip" />
                                </button>
                                {menuAdjuntoOpen && (
                                    <div className="chat-interno-menu-adjunto">
                                        <button type="button" className="chat-interno-menu-adjunto-item" onClick={() => { inputImagenRef.current?.click(); setMenuAdjuntoOpen(false); }}>
                                            <i className="bi bi-image" /> Imagen
                                        </button>
                                        <button type="button" className="chat-interno-menu-adjunto-item" onClick={abrirSelectorTramite}>
                                            <i className="bi bi-folder-fill" /> Trámite
                                        </button>
                                        <button type="button" className="chat-interno-menu-adjunto-item" onClick={abrirSelectorCliente}>
                                            <i className="bi bi-person-fill" /> Cliente
                                        </button>
                                        <button type="button" className="chat-interno-menu-adjunto-item" onClick={() => enviarInfoSistema('Próximos eventos', '/calendario', 'Ver agenda y eventos')}>
                                            <i className="bi bi-calendar-event" /> Próximos eventos
                                        </button>
                                        <button type="button" className="chat-interno-menu-adjunto-item" onClick={() => enviarInfoSistema('Reportes de trámites', '/reportes/tramites', 'Ver reportes')}>
                                            <i className="bi bi-bar-chart-fill" /> Reportes trámites
                                        </button>
                                        <button type="button" className="chat-interno-menu-adjunto-item" onClick={() => enviarInfoSistema('Reportes de gastos', '/reportes/gastos', 'Ver gastos médicos')}>
                                            <i className="bi bi-currency-dollar" /> Reportes gastos
                                        </button>
                                    </div>
                                )}
                            </div>
                            <input
                                type="text"
                                className="chat-interno-input-msg"
                                placeholder="Escribe un mensaje..."
                                value={textoMensaje}
                                onChange={(e) => setTextoMensaje(e.target.value)}
                                aria-label="Escribe un mensaje"
                            />
                            <button type="submit" className="chat-interno-btn-enviar" disabled={!textoMensaje.trim() || !conversacionActiva?.id} aria-label="Enviar">
                                <i className="bi bi-send-fill" />
                            </button>
                        </form>

                        {detalleOpen && (
                            <div className="chat-interno-selector-overlay" onClick={cerrarDetalle}>
                                <div className="chat-interno-selector-panel chat-interno-modal-detalle" onClick={(e) => e.stopPropagation()}>
                                    <div className="chat-interno-selector-header">
                                        <span>{detalleConversacion?.tipo === 'GRUPO' ? 'Detalle del grupo' : 'Detalle del contacto'}</span>
                                        <button type="button" className="chat-interno-close" onClick={cerrarDetalle}><i className="bi bi-x-lg" /></button>
                                    </div>
                                    <div className="chat-interno-modal-crear-grupo-body">
                                        {loadingDetalle ? (
                                            <div className="chat-interno-empty">Cargando…</div>
                                        ) : detalleConversacion?.tipo === 'GRUPO' ? (
                                            <>
                                                <div className="chat-interno-detalle-grupo">
                                                    {editandoNombreGrupo ? (
                                                        <div className="chat-interno-edit-nombre">
                                                            <input
                                                                type="text"
                                                                className="chat-interno-input-nombre-grupo"
                                                                value={nombreGrupoEdit}
                                                                onChange={(e) => setNombreGrupoEdit(e.target.value)}
                                                                autoFocus
                                                            />
                                                            <div className="chat-interno-edit-nombre-btns">
                                                                <button type="button" className="chat-interno-btn-cancelar" onClick={() => { setEditandoNombreGrupo(false); setNombreGrupoEdit(detalleConversacion.nombre || ''); }}>Cancelar</button>
                                                                <button type="button" className="chat-interno-btn-crear-grupo-submit" onClick={guardarNombreGrupo}>Guardar</button>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <p className="chat-interno-detalle-nombre"><strong>{detalleConversacion.nombre || 'Grupo'}</strong></p>
                                                    )}
                                                </div>
                                                <label className="chat-interno-label">Integrantes ({detalleConversacion.participantes?.length || 0})</label>
                                                <ul className="chat-interno-lista chat-interno-lista-integrantes">
                                                    {(detalleConversacion.participantes || []).map((p) => (
                                                        <li key={p.idUsuario} className="chat-interno-integrante">
                                                            <div className="chat-interno-item-avatar chat-interno-avatar-sm">{inicialNombre({ nombre: p.nombre })}</div>
                                                            <div className="chat-interno-item-info flex-grow-1">
                                                                <div className="chat-interno-item-nombre">{p.nombre || p.email || 'Usuario'}</div>
                                                                {p.email && <div className="chat-interno-item-preview">{p.email}</div>}
                                                            </div>
                                                            {p.idUsuario !== user?.id && (
                                                                <button type="button" className="chat-interno-btn-quitar" onClick={() => quitarParticipanteGrupo(p.idUsuario)} title="Quitar del grupo">
                                                                    <i className="bi bi-person-dash" />
                                                                </button>
                                                            )}
                                                        </li>
                                                    ))}
                                                </ul>
                                                <div className="chat-interno-detalle-acciones">
                                                    <button type="button" className={`chat-interno-btn-detalle ${detalleConversacion.esDestacado ? 'chat-interno-btn-destacado-activo' : ''}`} onClick={toggleDestacar} title={detalleConversacion.esDestacado ? 'Quitar de destacados' : 'Añadir a destacados'}>
                                                        <i className={detalleConversacion.esDestacado ? 'bi bi-star-fill' : 'bi bi-star'} /> {detalleConversacion.esDestacado ? 'Quitar de destacados' : 'Añadir a destacados'}
                                                    </button>
                                                    <button type="button" className="chat-interno-btn-detalle" onClick={() => setAgregarUsuarioGrupoOpen(true)}>
                                                        <i className="bi bi-person-plus" /> Agregar usuario
                                                    </button>
                                                    {!editandoNombreGrupo && (
                                                        <button type="button" className="chat-interno-btn-detalle" onClick={() => setEditandoNombreGrupo(true)}>
                                                            <i className="bi bi-pencil" /> Modificar grupo
                                                        </button>
                                                    )}
                                                    <button type="button" className="chat-interno-btn-detalle chat-interno-btn-eliminar" onClick={eliminarGrupo}>
                                                        <i className="bi bi-trash" /> Eliminar grupo
                                                    </button>
                                                </div>
                                                {agregarUsuarioGrupoOpen && (
                                                    <div className="chat-interno-agregar-usuario-wrap">
                                                        <label className="chat-interno-label">Seleccionar usuario a agregar</label>
                                                        <div className="chat-interno-lista-participantes">
                                                            {usuariosSistema
                                                                .filter((u) => !(detalleConversacion.participantes || []).some((p) => p.idUsuario === u.id))
                                                                .map((u) => (
                                                                    <button key={u.id} type="button" className="chat-interno-participante-item" onClick={() => agregarUsuarioAlGrupo(u.id)}>
                                                                        {u.nombre || u.email || 'Usuario'}
                                                                    </button>
                                                                ))}
                                                            {usuariosSistema.filter((u) => !(detalleConversacion.participantes || []).some((p) => p.idUsuario === u.id)).length === 0 && (
                                                                <div className="chat-interno-empty small">No hay más usuarios para agregar</div>
                                                            )}
                                                        </div>
                                                        <button type="button" className="chat-interno-btn-cancelar mt-2" onClick={() => setAgregarUsuarioGrupoOpen(false)}>Cerrar</button>
                                                    </div>
                                                )}
                                            </>
                                        ) : (
                                            <>
                                                {(detalleConversacion?.participantes || []).filter((p) => p.idUsuario !== user?.id).map((p) => (
                                                    <div key={p.idUsuario} className="chat-interno-detalle-usuario">
                                                        <div className="chat-interno-item-avatar chat-interno-avatar-header">{inicialNombre({ nombre: p.nombre })}</div>
                                                        <div className="chat-interno-detalle-usuario-info">
                                                            <strong>{p.nombre || 'Usuario'}</strong>
                                                            {p.email && <div className="text-muted small">{p.email}</div>}
                                                        </div>
                                                    </div>
                                                ))}
                                                <div className="chat-interno-detalle-acciones">
                                                    <button type="button" className={`chat-interno-btn-detalle ${detalleConversacion.esDestacado ? 'chat-interno-btn-destacado-activo' : ''}`} onClick={toggleDestacar} title={detalleConversacion.esDestacado ? 'Quitar de destacados' : 'Añadir a destacados'}>
                                                        <i className={detalleConversacion.esDestacado ? 'bi bi-star-fill' : 'bi bi-star'} /> {detalleConversacion.esDestacado ? 'Quitar de destacados' : 'Añadir a destacados'}
                                                    </button>
                                                    <button type="button" className="chat-interno-btn-detalle chat-interno-btn-eliminar" onClick={eliminarConversacionPrivada}>
                                                        <i className="bi bi-trash" /> Eliminar conversación
                                                    </button>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                    </>
                ) : (
                    <>
                <div className="chat-interno-header">
                    <h3>Chat interno</h3>
                    <button type="button" className="chat-interno-close" onClick={onClose} aria-label="Cerrar">
                        <i className="bi bi-x-lg" />
                    </button>
                </div>

                <div className="chat-interno-search-wrap">
                    <div className="chat-interno-search">
                        <i className="bi bi-search" />
                        <input
                            type="text"
                            placeholder="Buscar usuario para enviar mensaje..."
                            value={busqueda}
                            onChange={(e) => setBusqueda(e.target.value)}
                            aria-label="Buscar usuarios del sistema"
                        />
                    </div>
                    {busqueda.trim() && (
                        <div className="chat-interno-resultados-busqueda">
                            {loadingUsuarios ? (
                                <div className="chat-interno-empty">Cargando usuarios…</div>
                            ) : usuariosBusqueda.length === 0 ? (
                                <div className="chat-interno-empty">Ningún usuario coincide con &quot;{busqueda.trim()}&quot;</div>
                            ) : (
                                <ul className="chat-interno-lista">
                                    <li className="chat-interno-busqueda-titulo">Usuarios del sistema</li>
                                {usuariosBusqueda.map((u) => (
                                    <li key={u.id}>
                                        <button
                                            type="button"
                                            className="chat-interno-item"
                                            onClick={(e) => { e.stopPropagation(); e.preventDefault(); iniciarChatConUsuario(u); }}
                                        >
                                            <div className="chat-interno-item-avatar">{inicialNombre(u)}</div>
                                            <div className="chat-interno-item-info">
                                                <div className="chat-interno-item-nombre">{u.nombre || u.email || 'Usuario'}</div>
                                                {u.email && u.nombre && <div className="chat-interno-item-preview">{u.email}</div>}
                                            </div>
                                            <i className="bi bi-chat-dots ms-2 text-primary" title="Enviar mensaje" />
                                        </button>
                                    </li>
                                ))}
                                </ul>
                            )}
                        </div>
                    )}
                </div>

                <div className="chat-interno-secciones">
                    {/* Destacados */}
                    <div className={`chat-interno-seccion ${seccionesOpen.destacados ? 'open' : ''}`}>
                        <button
                            type="button"
                            className={`chat-interno-seccion-header ${seccionesOpen.destacados ? 'activa' : ''}`}
                            onClick={() => toggleSeccion('destacados')}
                        >
                            <span><i className="bi bi-star-fill" /> Destacados ({destacados.length})</span>
                            <i className="bi bi-chevron-right chat-interno-seccion-chevron" />
                        </button>
                        <div className="chat-interno-seccion-contenido">
                            <ul className="chat-interno-lista">
                                {loading ? (
                                    <li className="chat-interno-empty">Cargando…</li>
                                ) : destacadosF.length === 0 ? (
                                    <li className="chat-interno-empty">Ninguna conversación destacada</li>
                                ) : (
                                    destacadosF.map((item) => (
                                        <li key={item.id}>
                                            <button
                                                type="button"
                                                className="chat-interno-item"
                                                onClick={() => abrirConversacion('destacado', item.id, getNombreConversacion(item))}
                                            >
                                                <div className="chat-interno-item-avatar">{inicialNombre({ nombre: getNombreConversacion(item) })}</div>
                                                <div className="chat-interno-item-info">
                                                    <div className="chat-interno-item-nombre">{getNombreConversacion(item)}</div>
                                                    {getPreviewUltimoMensaje(item.ultimoMensaje) && <div className="chat-interno-item-preview">{getPreviewUltimoMensaje(item.ultimoMensaje)}</div>}
                                                </div>
                                                {(item.noLeidos ?? 0) > 0 && <span className="chat-interno-badge-no-leidos">{item.noLeidos > 99 ? '99+' : item.noLeidos}</span>}
                                            </button>
                                        </li>
                                    ))
                                )}
                            </ul>
                        </div>
                    </div>

                    {/* Grupales: manuales + en el futuro grupos por área (se crearán al asignar área al usuario) */}
                    <div className={`chat-interno-seccion ${seccionesOpen.grupales ? 'open' : ''}`}>
                        <button
                            type="button"
                            className={`chat-interno-seccion-header ${seccionesOpen.grupales ? 'activa' : ''}`}
                            onClick={() => toggleSeccion('grupales')}
                        >
                            <span><i className="bi bi-people-fill" /> Grupales ({grupales.length})</span>
                            <i className="bi bi-chevron-right chat-interno-seccion-chevron" />
                        </button>
                        <div className="chat-interno-seccion-contenido">
                            <ul className="chat-interno-lista">
                                {loading ? (
                                    <li className="chat-interno-empty">Cargando…</li>
                                ) : grupalesF.length === 0 ? (
                                    <>
                                        <li className="chat-interno-empty">Sin grupos. Crea uno o aparecerán aquí los grupos por área cuando asignes área a los usuarios.</li>
                                        <li className="chat-interno-crear-grupo-wrap">
                                            <button type="button" className="chat-interno-btn-crear-grupo" onClick={() => setCrearGrupoOpen(true)}>
                                                <i className="bi bi-plus-circle" /> Crear grupo
                                            </button>
                                        </li>
                                    </>
                                ) : (
                                    <>
                                        {grupalesF.map((item) => (
                                            <li key={item.id}>
                                                <button
                                                    type="button"
                                                    className="chat-interno-item"
                                                    onClick={() => abrirConversacion('grupo', item.id, getNombreConversacion(item))}
                                                >
                                                    <div className="chat-interno-item-avatar">{inicialNombre({ nombre: getNombreConversacion(item) })}</div>
                                                    <div className="chat-interno-item-info">
                                                        <div className="chat-interno-item-nombre">{getNombreConversacion(item)}</div>
                                                        {getPreviewUltimoMensaje(item.ultimoMensaje) && <div className="chat-interno-item-preview">{getPreviewUltimoMensaje(item.ultimoMensaje)}</div>}
                                                    </div>
                                                    {(item.noLeidos ?? 0) > 0 && <span className="chat-interno-badge-no-leidos">{item.noLeidos > 99 ? '99+' : item.noLeidos}</span>}
                                                </button>
                                            </li>
                                        ))}
                                        <li className="chat-interno-crear-grupo-wrap">
                                            <button type="button" className="chat-interno-btn-crear-grupo chat-interno-btn-crear-grupo-secondary" onClick={() => setCrearGrupoOpen(true)}>
                                                <i className="bi bi-plus-circle" /> Crear otro grupo
                                            </button>
                                        </li>
                                    </>
                                )}
                            </ul>
                        </div>
                    </div>

                    {/* Privados */}
                    <div className={`chat-interno-seccion ${seccionesOpen.privados ? 'open' : ''}`}>
                        <button
                            type="button"
                            className={`chat-interno-seccion-header ${seccionesOpen.privados ? 'activa' : ''}`}
                            onClick={() => toggleSeccion('privados')}
                        >
                            <span><i className="bi bi-chat-dots-fill" /> Privados ({privados.length})</span>
                            <i className="bi bi-chevron-right chat-interno-seccion-chevron" />
                        </button>
                        <div className="chat-interno-seccion-contenido">
                            <ul className="chat-interno-lista">
                                {loading ? (
                                    <li className="chat-interno-empty">Cargando…</li>
                                ) : privadosF.length === 0 ? (
                                    <li className="chat-interno-empty">Sin conversaciones privadas</li>
                                ) : (
                                    privadosF.map((item) => (
                                        <li key={item.id}>
                                            <button
                                                type="button"
                                                className="chat-interno-item"
                                                onClick={() => abrirConversacion('privado', item.id, getNombreConversacion(item))}
                                            >
                                                <div className="chat-interno-item-avatar">{inicialNombre({ nombre: getNombreConversacion(item) })}</div>
                                                <div className="chat-interno-item-info">
                                                    <div className="chat-interno-item-nombre">{getNombreConversacion(item)}</div>
                                                    {getPreviewUltimoMensaje(item.ultimoMensaje) && <div className="chat-interno-item-preview">{getPreviewUltimoMensaje(item.ultimoMensaje)}</div>}
                                                </div>
                                                {(item.noLeidos ?? 0) > 0 && <span className="chat-interno-badge-no-leidos">{item.noLeidos > 99 ? '99+' : item.noLeidos}</span>}
                                            </button>
                                        </li>
                                    ))
                                )}
                            </ul>
                        </div>
                    </div>
                </div>

                <div className="chat-interno-footer">
                    <button type="button" className="chat-interno-ver-todo" onClick={onClose}>
                        Ver todo
                    </button>
                </div>

                {crearGrupoOpen && (
                    <div className="chat-interno-selector-overlay" onClick={cerrarModalCrearGrupo}>
                        <div className="chat-interno-selector-panel chat-interno-modal-crear-grupo" onClick={(ev) => ev.stopPropagation()}>
                            <div className="chat-interno-selector-header">
                                <span>Crear grupo</span>
                                <button type="button" className="chat-interno-close" onClick={cerrarModalCrearGrupo}><i className="bi bi-x-lg" /></button>
                            </div>
                            <div className="chat-interno-modal-crear-grupo-body">
                                <label className="chat-interno-label">Nombre del grupo</label>
                                <input
                                    type="text"
                                    className="chat-interno-input-nombre-grupo"
                                    placeholder="Ej: Equipo RTI"
                                    value={nombreNuevoGrupo}
                                    onChange={(e) => setNombreNuevoGrupo(e.target.value)}
                                />
                                <label className="chat-interno-label">Agregar participantes</label>
                                <div className="chat-interno-lista-participantes">
                                    {usuariosSistema.length === 0 ? (
                                        <div className="chat-interno-empty">Cargando usuarios…</div>
                                    ) : (
                                        usuariosSistema.map((u) => (
                                            <label key={u.id} className="chat-interno-participante-item">
                                                <input
                                                    type="checkbox"
                                                    checked={participantesSeleccionados.includes(u.id)}
                                                    onChange={() => toggleParticipanteGrupo(u.id)}
                                                />
                                                <span className="chat-interno-participante-nombre">{u.nombre || u.email || 'Usuario'}</span>
                                            </label>
                                        ))
                                    )}
                                </div>
                            </div>
                            <div className="chat-interno-modal-crear-grupo-footer">
                                <button type="button" className="chat-interno-btn-cancelar" onClick={cerrarModalCrearGrupo}>Cancelar</button>
                                <button type="button" className="chat-interno-btn-crear-grupo-submit" onClick={crearGrupo} disabled={!nombreNuevoGrupo.trim() || creandoGrupo}>
                                    {creandoGrupo ? 'Creando…' : 'Crear grupo'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
                    </>
                )}
            </div>

            {confirmModal.open && (
                <div className="chat-interno-selector-overlay chat-interno-confirm-overlay" onClick={() => confirmModal.onCancel?.()}>
                    <div className="chat-interno-selector-panel chat-interno-confirm-panel" onClick={(e) => e.stopPropagation()}>
                        <div className="chat-interno-confirm-title">{confirmModal.title}</div>
                        <p className="chat-interno-confirm-message">{confirmModal.message}</p>
                        <div className="chat-interno-confirm-actions">
                            <button type="button" className="chat-interno-btn-cancelar" onClick={() => confirmModal.onCancel?.()}>Cancelar</button>
                            <button type="button" className="chat-interno-btn-crear-grupo-submit" onClick={() => confirmModal.onConfirm?.()}>Aceptar</button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default ChatInternoPanel;

